/**
 * Utilities for building context data from Recoil state
 */

import { Permissions } from 'src/app_deprecated/constants/PermissionsConstants';

import type {
  OrganizationContext,
  PermissionsContext,
  ProductFull,
  ProductsData,
  ProductsSummary,
  UserContext,
} from './types';
import type { LocProfile } from 'src/app/state/types/loc-profile';
import type { UserState } from 'src/app/state/user';
import type { SelectedCompany, SelectedLocation } from 'src/app/state/user-dispensaries';
import type { PermissionVals } from 'src/app/state/user-permissions';

/**
 * Build user profile context from user atom
 */
export function buildUserContext(user: UserState): UserContext | null {
  if (!user?.Id) {
    return null;
  }

  return {
    userId: user.Id?.toString() ?? '',
    userName: user.FullName,
    isAdministrator: user.Administrator ?? false,
    isManager: user.ManagerFlag ?? false,
    // Explicitly exclude sensitive data like email, password, tokens
  };
}

/**
 * Build organization context from dispensaries atom
 */
export function buildOrganizationContext(
  selectedLocation: SelectedLocation,
  selectedCompany: SelectedCompany,
  locProfile: LocProfile
): OrganizationContext | null {
  if (!selectedLocation?.LocId || !selectedCompany?.LspId) {
    return null;
  }

  // Extract enabled features from locProfile
  const enabledFeatures = Object.keys(locProfile)
    .filter((key) => locProfile[key] === true)
    .filter(
      (key) => key.startsWith('Has') || key.startsWith('Use') || key.startsWith('Enable') || key.startsWith('Show')
    );

  return {
    location: {
      id: selectedLocation.LocId,
      name: selectedLocation.Name,
      state: selectedLocation.State,
      isLive: selectedLocation.IsLive,
      isSandbox: selectedLocation.IsSandbox,
    },
    company: {
      id: selectedCompany.LspId.toString(),
      name: selectedCompany.LspName,
      orgId: selectedCompany.OrgId?.toString() ?? '',
    },
    features: enabledFeatures,
    integrations: {
      hasBioTrack: locProfile.HasBioTrack,
      hasMetrc: locProfile.HasMETRC,
      hasPaymentsHub: locProfile.HasPaymentsHub,
      useMETRC: locProfile.UseMETRC,
      useBioTrack: locProfile.UseBioTrack,
    },
    capabilities: {
      dualPricing: locProfile.DualPricing,
      manufacturing: locProfile.Manufacturing,
      delivery: locProfile.EnableDelivery,
      cultivation: locProfile.EnablePlantSearch,
      taxInclusivePricing: locProfile.TaxInclusivePricing,
    },
  };
}

/**
 * Build permissions context from permissions atom
 */
export function buildPermissionsContext(perms: Record<PermissionVals, PermissionVals>[]): PermissionsContext | null {
  if (!perms || Object.keys(perms).length === 0) {
    return null;
  }

  // Extract granted permissions by presence of keys
  const grantedPermissions = Object.keys(perms);

  const canEditProductDetail = !!perms[Permissions.EditProductDetail];
  const canViewStaticReports = !!perms[Permissions.ViewStaticReports];
  const canEditPricing = !!perms[Permissions.EditPricing];
  const canEditInventoryStatus = !!perms[Permissions.EditInventoryStatus];
  const canEditCustomers = !!perms[Permissions.EditCustomers];
  const canEditDiscounts = !!perms[Permissions.EditDiscounts];
  const canEditUsers = !!perms[Permissions.EditUsers];
  const canViewMarketingReports = !!perms[Permissions.ViewMarketingReports];

  // Also create a simplified "capabilities" object for common checks
  const capabilities = {
    canEditProducts: canEditProductDetail,
    canViewReports: canViewStaticReports,
    canEditPricing,
    canEditInventory: canEditInventoryStatus,
    canEditCustomers,
    canEditDiscounts,
    canEditUsers,
    canViewFinancials: canViewMarketingReports,
  };

  return {
    grantedPermissions,
    capabilities,
    permissionCount: grantedPermissions.length,
  };
}

/**
 * Build products catalog summary (for large datasets)
 */
export function buildProductsSummary(products: ProductFull[], loading: boolean): ProductsSummary {
  const productsCount = products.length;

  return {
    totalProducts: productsCount,
    loading,
    categories: Array.from(new Set(products.map((p) => p.Category).filter(Boolean))),
    brands: Array.from(new Set(products.map((p) => p.BrandName).filter(Boolean))),
    productTypes: Array.from(new Set(products.map((p) => p.Type).filter(Boolean))),
    priceRange: {
      min: Math.min(...products.filter((p) => p.Price).map((p) => p.Price)),
      max: Math.max(...products.map((p) => p.Price || 0)),
    },
    // Sample of first 10 products for context
    sampleProducts: products.slice(0, 10).map((p) => ({
      ProductId: p.ProductId,
      Name: p.Name,
      Sku: p.Sku,
      Category: p.Category,
      Price: p.Price,
      Available: p.Available,
      BrandName: p.BrandName,
    })),
    _note: `This is a summary. Full catalog has ${productsCount} products. Use request_additional_context if you need complete product details.`,
  };
}

/**
 * Build full products catalog data (for smaller datasets)
 */
export function buildProductsData(products: ProductFull[], loading: boolean): ProductsData {
  return {
    totalProducts: products.length,
    loading,
    products: products.map((p) => ({
      ProductId: p.ProductId,
      Name: p.Name,
      Sku: p.Sku,
      Category: p.Category,
      Price: p.Price,
      RecPrice: p.RecPrice,
      Cost: p.Cost,
      Available: p.Available,
      BrandName: p.BrandName,
      Vendor: p.Vendor,
      Type: p.Type,
      IsRetired: p.IsRetired,
      ProductTags: p.ProductTags,
      THCContent: p.THCContent,
      CBDContent: p.CBDContent,
    })),
  };
}
